import { BOARD_SIZE, MAX_LEVEL } from "./constants";
import type { BoardSize, BoardState, LevelConfig, SoloMode, SoloStageGoal, SoloStageTelemetry } from "./types";
import type { Rng } from "./rng";

const TURBO_SCORE_TARGETS = [
  10800,
  12000,
  13200,
  14400,
  15600,
  16800,
  18000,
  19200,
  20400,
  21600,
  20400,
  21600,
  22800,
  24000,
  25200,
  24000,
  25200,
  26400,
  27600,
  28800
] as const;

interface SoloModeRules {
  boardSize: BoardSize;
  rotationEveryBeatsMultiplier: number;
  animationDurationMultiplier: number;
  doomPressureMultiplier: number;
  grooveChargeMultiplier: number;
  usesClassicProgression: boolean;
  tutorialEligible: boolean;
  scoreTargets?: ReadonlyArray<number>;
}

export const SOLO_MODES: SoloMode[] = ["classic", "row-rush", "clearout", "color-hunt", "turbomaxxing"];

/** Modes shown in the solo select carousel. */
export const SOLO_SELECT_MODES: SoloMode[] = ["classic", "row-rush", "clearout", "color-hunt", "turbomaxxing"];

const SOLO_MODE_UNLOCK_LEVELS: Record<SoloMode, number> = {
  classic: 1,
  "row-rush": 3,
  clearout: 5,
  "color-hunt": 10,
  turbomaxxing: 20
};

const SOLO_MODE_RULES: Record<SoloMode, SoloModeRules> = {
  classic: {
    boardSize: BOARD_SIZE as BoardSize,
    rotationEveryBeatsMultiplier: 1,
    animationDurationMultiplier: 1,
    doomPressureMultiplier: 1,
    grooveChargeMultiplier: 1,
    usesClassicProgression: true,
    tutorialEligible: true
  },
  "row-rush": {
    boardSize: BOARD_SIZE as BoardSize,
    rotationEveryBeatsMultiplier: 1,
    animationDurationMultiplier: 1,
    doomPressureMultiplier: 1,
    grooveChargeMultiplier: 1,
    usesClassicProgression: false,
    tutorialEligible: false
  },
  clearout: {
    boardSize: BOARD_SIZE as BoardSize,
    rotationEveryBeatsMultiplier: 1,
    animationDurationMultiplier: 1,
    doomPressureMultiplier: 1,
    grooveChargeMultiplier: 1,
    usesClassicProgression: false,
    tutorialEligible: false
  },
  "color-hunt": {
    boardSize: BOARD_SIZE as BoardSize,
    rotationEveryBeatsMultiplier: 1,
    animationDurationMultiplier: 1,
    doomPressureMultiplier: 1,
    grooveChargeMultiplier: 1,
    usesClassicProgression: false,
    tutorialEligible: false
  },
  turbomaxxing: {
    boardSize: 7,
    rotationEveryBeatsMultiplier: 0.5,
    animationDurationMultiplier: 0.5,
    doomPressureMultiplier: 2,
    grooveChargeMultiplier: 2,
    usesClassicProgression: true,
    tutorialEligible: false,
    scoreTargets: TURBO_SCORE_TARGETS
  }
};

export function soloModeRules(mode: SoloMode): SoloModeRules {
  return SOLO_MODE_RULES[mode];
}

export function soloModeBoardSize(mode: SoloMode): BoardSize {
  return soloModeRules(mode).boardSize;
}

export function soloModeAnimationDurationMultiplier(mode: SoloMode): number {
  return soloModeRules(mode).animationDurationMultiplier;
}

export function soloModeDoomPressureMultiplier(mode: SoloMode): number {
  return soloModeRules(mode).doomPressureMultiplier;
}

export function soloModeGrooveChargeMultiplier(mode: SoloMode): number {
  return soloModeRules(mode).grooveChargeMultiplier;
}

export function soloModeUsesClassicProgression(mode: SoloMode): boolean {
  return soloModeRules(mode).usesClassicProgression;
}

export function soloModeTutorialEligible(mode: SoloMode): boolean {
  return soloModeRules(mode).tutorialEligible;
}

export function levelConfigForSoloMode(mode: SoloMode, base: LevelConfig): LevelConfig {
  const rules = soloModeRules(mode);
  const modeTarget = rules.scoreTargets?.[base.level - 1];
  return {
    ...base,
    scoreTarget: modeTarget ?? base.scoreTarget,
    rotationEveryBeats: base.rotationEveryBeats * rules.rotationEveryBeatsMultiplier
  };
}

export function soloModeLabel(mode: SoloMode): string {
  if (mode === "classic") {
    return "CLASSIC";
  }
  if (mode === "row-rush") {
    return "ROW RUSH";
  }
  if (mode === "clearout") {
    return "CLEAROUT";
  }
  if (mode === "color-hunt") {
    return "COLOR HUNT";
  }
  return "TURBOMAXXING";
}

function wholeAccountLevel(level: number): number {
  return Math.max(1, Math.floor(level));
}

export function soloModeUnlockLevel(mode: SoloMode): number {
  return SOLO_MODE_UNLOCK_LEVELS[mode];
}

export function isSoloModeUnlocked(level: number, mode: SoloMode): boolean {
  return wholeAccountLevel(level) >= soloModeUnlockLevel(mode);
}

/** Return modes that were locked at `oldLevel` but are unlocked at `newLevel`, in unlock order. */
export function modesNewlyUnlocked(oldLevel: number, newLevel: number): SoloMode[] {
  return SOLO_SELECT_MODES.filter(
    (m) => !isSoloModeUnlocked(oldLevel, m) && isSoloModeUnlocked(newLevel, m)
  );
}

export function soloModeAccent(mode: SoloMode): string {
  if (mode === "classic") {
    return "#5c8cd4";
  }
  if (mode === "row-rush") {
    return "#ff8a50";
  }
  if (mode === "clearout") {
    return "#7cff9c";
  }
  if (mode === "color-hunt") {
    return "#ffd75a";
  }
  return "#ff5b99";
}

export function soloModeDescription(mode: SoloMode): string {
  if (mode === "classic") {
    return "CURRENT RULES";
  }
  if (mode === "row-rush") {
    return "20 ROUND SPRINT";
  }
  if (mode === "clearout") {
    return "EMPTY THE BOARD";
  }
  if (mode === "color-hunt") {
    return "HUNT TARGET COLORS";
  }
  return "7X7 DOUBLE-SPEED";
}

export function rowRushTargetRowCount(level: number): number {
  if (level <= 5) {
    return 4;
  }
  if (level <= 10) {
    return 3;
  }
  if (level <= 15) {
    return 2;
  }
  return 1;
}

export function rowRushQuota(level: number): number {
  return 12 + 2 * Math.max(0, level - 1);
}

function rowRushPerfectCoverageTarget(targetRows: number[], boardSize: number): number {
  return Math.max(0, targetRows.length) * Math.max(0, boardSize);
}

export function colorHuntTargetColorCount(level: number): number {
  return level <= 10 ? 2 : 1;
}

export function colorHuntQuota(level: number, seriesIndex = 0): number {
  const baseQuota = 24 + 4 * Math.max(0, level - 1);
  if (level < MAX_LEVEL) {
    return baseQuota;
  }
  return baseQuota + 4 * Math.max(0, Math.floor(seriesIndex));
}

export interface CreateSoloStageGoalOptions {
  previousTargetRows?: number[];
  previousTargetColors?: number[];
}

function randomUniqueRange(count: number, maxExclusive: number, rng: Rng): number[] {
  const values = Array.from({ length: Math.max(0, maxExclusive) }, (_, idx) => idx);
  for (let i = values.length - 1; i > 0; i -= 1) {
    const swapIdx = rng.nextInt(i + 1);
    const temp = values[i];
    values[i] = values[swapIdx] ?? values[i];
    values[swapIdx] = temp;
  }
  return values.slice(0, Math.max(0, Math.min(count, values.length))).sort((a, b) => a - b);
}

function randomColorHuntTargets(
  level: number,
  tileTypes: number,
  rng: Rng,
  previousTargetColors: number[] = []
): number[] {
  const targetCount = colorHuntTargetColorCount(level);
  const maxExclusive = Math.max(1, tileTypes);
  const allColors = Array.from({ length: maxExclusive }, (_, idx) => idx);
  const normalizedPreviousColors = normalizeUniqueValues(previousTargetColors, maxExclusive, maxExclusive);
  if (normalizedPreviousColors.length === 0) {
    return randomUniqueRange(targetCount, maxExclusive, rng);
  }
  const previousSet = new Set(normalizedPreviousColors);
  const preferredColors = allColors.filter((color) => !previousSet.has(color));
  const selectedColors = pickUniqueValuesFromPool(preferredColors, targetCount, rng);
  if (selectedColors.length >= targetCount) {
    return selectedColors;
  }
  const selectedSet = new Set(selectedColors);
  const fallbackColors = normalizedPreviousColors.filter((color) => !selectedSet.has(color));
  return [
    ...selectedColors,
    ...pickUniqueValuesFromPool(fallbackColors, targetCount - selectedColors.length, rng)
  ].sort((a, b) => a - b);
}

function pickUniqueValuesFromPool(pool: number[], count: number, rng: Rng): number[] {
  const uniquePool = [...new Set(pool)];
  rng.shuffleInPlace(uniquePool);
  return uniquePool.slice(0, Math.max(0, Math.min(count, uniquePool.length))).sort((a, b) => a - b);
}

function randomRowRushTargetRows(
  level: number,
  boardSize: number,
  rng: Rng,
  previousTargetRows: number[] = []
): number[] {
  const targetCount = Math.max(0, Math.min(rowRushTargetRowCount(level), Math.max(0, boardSize)));
  if (targetCount === 0) {
    return [];
  }
  const allRows = Array.from({ length: Math.max(0, boardSize) }, (_, idx) => idx);
  const previousSet = new Set(normalizeUniqueValues(previousTargetRows, boardSize, boardSize));
  const preferredRows = allRows.filter((row) => !previousSet.has(row));
  const selectedRows = pickUniqueValuesFromPool(preferredRows, targetCount, rng);
  if (selectedRows.length >= targetCount) {
    return selectedRows;
  }
  const selectedSet = new Set(selectedRows);
  const fallbackRows = allRows.filter((row) => !selectedSet.has(row));
  return [
    ...selectedRows,
    ...pickUniqueValuesFromPool(fallbackRows, targetCount - selectedRows.length, rng)
  ].sort((a, b) => a - b);
}

export function createColorHuntStageGoal(
  level: number,
  tileTypes: number,
  rng: Rng,
  options?: { seriesIndex?: number; previousTargetColors?: number[] }
): Extract<SoloStageGoal, { mode: "color-hunt" }> {
  const seriesIndex = level >= MAX_LEVEL ? Math.max(0, Math.floor(options?.seriesIndex ?? 0)) : 0;
  return {
    mode: "color-hunt",
    progress: 0,
    target: colorHuntQuota(level, seriesIndex),
    targetColors: randomColorHuntTargets(level, Math.max(1, tileTypes), rng, options?.previousTargetColors),
    seriesIndex
  };
}

export function countOccupiedCells(board: BoardState): number {
  let total = 0;
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      if (board.cells[row][col].kind !== "EMPTY") {
        total += 1;
      }
    }
  }
  return total;
}

export function createSoloStageGoal(
  mode: SoloMode,
  level: number,
  tileTypes: number,
  rng: Rng,
  board: BoardState,
  options?: CreateSoloStageGoalOptions
): SoloStageGoal | null {
  if (soloModeUsesClassicProgression(mode)) {
    return null;
  }
  if (mode === "row-rush") {
    const targetRows = randomRowRushTargetRows(level, board.size, rng, options?.previousTargetRows);
    return {
      mode,
      progress: 0,
      target: rowRushPerfectCoverageTarget(targetRows, board.size),
      targetRows,
      scoredTargetCellKeys: []
    };
  }
  if (mode === "color-hunt") {
    return createColorHuntStageGoal(level, tileTypes, rng, {
      previousTargetColors: options?.previousTargetColors
    });
  }
  if (mode !== "clearout") {
    return null;
  }
  const occupied = countOccupiedCells(board);
  return {
    mode,
    progress: 0,
    target: Math.max(1, occupied),
    initialOccupied: Math.max(1, occupied)
  };
}

function normalizeUniqueValues(values: number[], maxExclusive: number, expectedCount: number): number[] {
  const unique = [...new Set(values.filter((value) => Number.isInteger(value) && value >= 0 && value < maxExclusive))];
  return unique.slice(0, expectedCount).sort((a, b) => a - b);
}

export function normalizeSoloStageGoal(
  mode: SoloMode,
  level: number,
  tileTypes: number,
  goal: SoloStageGoal | null | undefined,
  rng: Rng,
  board: BoardState
): SoloStageGoal | null {
  if (soloModeUsesClassicProgression(mode)) {
    return null;
  }
  const fallback = createSoloStageGoal(mode, level, tileTypes, rng, board);
  if (fallback == null) {
    return null;
  }
  if (goal == null || goal.mode !== mode) {
    return fallback;
  }
  if (mode === "row-rush") {
    const rowGoal = goal as Extract<SoloStageGoal, { mode: "row-rush" }>;
    const fallbackGoal = fallback.mode === "row-rush" ? fallback : createSoloStageGoal("row-rush", level, tileTypes, rng, board);
    if (fallbackGoal == null || fallbackGoal.mode !== "row-rush") {
      return null;
    }
    const targetRows = normalizeUniqueValues(rowGoal.targetRows, board.size, rowRushTargetRowCount(level));
    const normalizedTargetRows = targetRows.length === rowRushTargetRowCount(level) ? targetRows : fallbackGoal.targetRows;
    const target = rowRushPerfectCoverageTarget(normalizedTargetRows, board.size);
    const scoredTargetCellKeys = [...new Set((rowGoal.scoredTargetCellKeys ?? []).filter((key) => {
      if (typeof key !== "string" || !key.includes(",")) {
        return false;
      }
      const [rowRaw, colRaw] = key.split(",");
      const row = Number(rowRaw);
      const col = Number(colRaw);
      return Number.isInteger(row)
        && Number.isInteger(col)
        && row >= 0
        && row < board.size
        && col >= 0
        && col < board.size
        && normalizedTargetRows.includes(row);
    }))].slice(0, target);
    return {
      mode,
      progress: Math.max(
        0,
        Math.min(
          target,
          Math.max(
            Math.floor(rowGoal.progress),
            scoredTargetCellKeys.length
          )
        )
      ),
      target,
      targetRows: normalizedTargetRows,
      scoredTargetCellKeys
    };
  }
  if (mode === "color-hunt") {
    const colorGoal = goal as Extract<SoloStageGoal, { mode: "color-hunt" }>;
    const baseQuota = colorHuntQuota(level);
    const derivedSeriesIndex = level >= MAX_LEVEL
      ? Math.max(
        0,
        Math.floor(colorGoal.seriesIndex ?? 0),
        Math.floor(Math.max(0, Math.floor((colorGoal.target ?? baseQuota) - baseQuota)) / 4)
      )
      : 0;
    const fallbackGoal = createColorHuntStageGoal(level, tileTypes, rng, { seriesIndex: derivedSeriesIndex });
    if (fallbackGoal == null || fallbackGoal.mode !== "color-hunt") {
      return null;
    }
    const targetColors = normalizeUniqueValues(colorGoal.targetColors, Math.max(1, tileTypes), colorHuntTargetColorCount(level));
    const target = colorHuntQuota(level, derivedSeriesIndex);
    return {
      mode,
      progress: Math.max(0, Math.min(target, Math.floor(colorGoal.progress))),
      target,
      targetColors: targetColors.length === colorHuntTargetColorCount(level) ? targetColors : fallbackGoal.targetColors,
      seriesIndex: derivedSeriesIndex
    };
  }
  const clearoutGoal = goal as Extract<SoloStageGoal, { mode: "clearout" }>;
  const occupied = countOccupiedCells(board);
  const target = Math.max(1, Math.floor(clearoutGoal.target || clearoutGoal.initialOccupied || occupied));
  return {
    mode,
    target,
    initialOccupied: target,
    progress: Math.max(0, Math.min(target, target - occupied))
  };
}

export function emptySoloStageTelemetry(boardSize: number = BOARD_SIZE): SoloStageTelemetry {
  return {
    clearedRows: Array.from({ length: Math.max(0, boardSize) }, () => 0),
    clearedColors: {},
    clearedTiles: 0
  };
}

export function mergeSoloStageTelemetry(
  base: SoloStageTelemetry | null | undefined,
  next: SoloStageTelemetry | null | undefined
): SoloStageTelemetry {
  const rowCount = Math.max(
    BOARD_SIZE,
    base?.clearedRows.length ?? 0,
    next?.clearedRows.length ?? 0
  );
  const out = emptySoloStageTelemetry(rowCount);
  const sources = [base, next];
  for (const source of sources) {
    if (source == null) {
      continue;
    }
    for (let row = 0; row < rowCount; row += 1) {
      out.clearedRows[row] += source.clearedRows[row] ?? 0;
    }
    for (const [colorKey, count] of Object.entries(source.clearedColors)) {
      out.clearedColors[colorKey] = (out.clearedColors[colorKey] ?? 0) + count;
    }
    out.clearedTiles += source.clearedTiles ?? 0;
  }
  return out;
}

export function hasSoloStageTelemetryValue(telemetry: SoloStageTelemetry): boolean {
  if (telemetry.clearedTiles > 0) {
    return true;
  }
  return Object.values(telemetry.clearedColors).some((value) => value > 0)
    || telemetry.clearedRows.some((value) => value > 0);
}
